/**
 * Lightweight offline queue for outreach quick-log submissions.
 *
 * Strategy: when navigator.onLine is true, POST normally. When false (or on
 * network failure / 5xx), the payload is appended to localStorage. A retry
 * loop fires on 'online' event and every 30s while items remain.
 *
 * Idempotency: every queued item carries a stable client_id; the backend
 * dedupes via {token_id, client_id}. So replays are safe.
 */

const QUEUE_KEY = 'nhf_outreach_offline_queue';

const readQueue = () => {
  try {
    return JSON.parse(localStorage.getItem(QUEUE_KEY) || '[]');
  } catch { return []; }
};
const writeQueue = (q) => {
  try { localStorage.setItem(QUEUE_KEY, JSON.stringify(q)); } catch { /* full disk, swallow */ }
};

export const queueSize = () => readQueue().length;

const newClientId = () =>
  `c_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;

/**
 * Submit a quick-log via volunteer token. If offline or POST fails with a
 * recoverable error, enqueue and retry later. Returns { ok, deduplicated, id, queued }.
 */
export const submitQuickLog = async ({ backendUrl, token, payload }) => {
  // Always ensure client_id for backend dedupe.
  const body = { ...payload, client_id: payload.client_id || newClientId() };

  if (!navigator.onLine) {
    enqueue(backendUrl, token, body);
    return { ok: true, queued: true, client_id: body.client_id };
  }

  try {
    const res = await fetch(`${backendUrl}/api/public/outreach/quick-log/${encodeURIComponent(token)}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      const j = await res.json();
      return { ok: true, queued: false, ...j, client_id: body.client_id };
    }
    if (res.status >= 500 || res.status === 0) {
      enqueue(backendUrl, token, body);
      return { ok: true, queued: true, client_id: body.client_id };
    }
    const err = await res.json().catch(() => ({ detail: `HTTP ${res.status}` }));
    return { ok: false, error: err.detail || `HTTP ${res.status}`, status: res.status };
  } catch (e) {
    enqueue(backendUrl, token, body);
    return { ok: true, queued: true, client_id: body.client_id };
  }
};

const enqueue = (backendUrl, token, body) => {
  const q = readQueue();
  q.push({ backendUrl, token, body, queued_at: new Date().toISOString() });
  writeQueue(q);
};

/**
 * Try to drain the queue. Items that succeed are removed; transient failures stay.
 * Items with permanent failures (4xx other than 408) are dropped to avoid loops.
 */
export const drainQueue = async () => {
  let q = readQueue();
  if (q.length === 0) return { drained: 0, remaining: 0 };
  let drained = 0;
  const remaining = [];

  for (const item of q) {
    try {
      const res = await fetch(
        `${item.backendUrl}/api/public/outreach/quick-log/${encodeURIComponent(item.token)}`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(item.body),
        },
      );
      if (res.ok) {
        drained += 1;
        continue;
      }
      if (res.status >= 500 || res.status === 408 || res.status === 0) {
        remaining.push(item); // transient
        continue;
      }
      // 4xx permanent (invalid/revoked token, bad payload) — drop silently
      drained += 1;
    } catch {
      remaining.push(item); // network glitch
    }
  }
  writeQueue(remaining);
  return { drained, remaining: remaining.length };
};

let drainInterval = null;

export const startOfflineSync = () => {
  if (typeof window === 'undefined') return;
  // Drain on regain-online
  window.addEventListener('online', () => { drainQueue().catch(() => {}); });
  // Periodic drain while items exist
  if (!drainInterval) {
    drainInterval = setInterval(() => {
      if (queueSize() > 0 && navigator.onLine) {
        drainQueue().catch(() => {});
      }
    }, 30_000);
  }
  // Initial attempt on load
  if (navigator.onLine && queueSize() > 0) {
    setTimeout(() => drainQueue().catch(() => {}), 1500);
  }
};

export const stopOfflineSync = () => {
  if (drainInterval) { clearInterval(drainInterval); drainInterval = null; }
};
